/**
 * MetaMask 原生 TRON（@metamask/connect-tron + Tron Wallet Snap）
 * 与 sun.io 一致：用户点击 → adapter.connect() → 等 adapter 完整 session（address + _scope）
 */

import { Buffer } from 'buffer'
import { base58de, resolveTronDisplayAddress } from './tron.mjs'
import { TRON_CHAIN_ID } from './tronInject.mjs'
import { isWalletUserCancel } from '../walletCancel.mjs'

if (typeof globalThis !== 'undefined' && !globalThis.Buffer) {
  globalThis.Buffer = Buffer
}

let adapterSingleton = null
let connectInflight = null

function sleepMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function getMetaMaskTronAdapter() {
  if (!adapterSingleton) {
    const { MetaMaskAdapter } = await import('@metamask/connect-tron')
    adapterSingleton = new MetaMaskAdapter()
  }
  return adapterSingleton
}

async function resetMetaMaskTronAdapter() {
  if (!adapterSingleton) return
  try {
    await adapterSingleton.disconnect()
  } catch (_e) {
    void _e
  }
  adapterSingleton = null
}

/** adapter 可签名：connected + address + multichain scope */
function isAdapterSessionReady(adapter) {
  if (!adapter) return false
  const addr = String(adapter.address || '').trim()
  return Boolean(adapter.connected && addr && adapter._scope)
}

function resolveMetaMaskTronAccountHex(address) {
  const raw = String(address || '').trim()
  if (!raw) return ''
  if (raw.startsWith('T')) {
    try {
      return base58de(raw)
    } catch (_e) {
      return ''
    }
  }
  if (/^0x[0-9a-fA-F]{40}$/i.test(raw)) return raw
  if (/^[0-9a-fA-F]{40}$/i.test(raw)) return `0x${raw}`
  return raw
}

async function waitForAdapterSessionReady(adapter, timeoutMs = 60000) {
  if (isAdapterSessionReady(adapter)) {
    return String(adapter.address).trim()
  }

  return new Promise((resolve) => {
    let settled = false
    let pollTimer = null
    let deadlineTimer = null

    const finish = (addr) => {
      if (settled) return
      settled = true
      if (pollTimer) clearInterval(pollTimer)
      if (deadlineTimer) clearTimeout(deadlineTimer)
      cleanupListeners()
      resolve(String(addr || '').trim())
    }

    const onEvent = () => {
      if (isAdapterSessionReady(adapter)) {
        finish(adapter.address)
      }
    }

    const cleanupListeners = () => {
      try {
        adapter?.off?.('connect', onEvent)
        adapter?.off?.('accountsChanged', onEvent)
      } catch (_e) {
        void _e
      }
    }

    try {
      adapter?.on?.('connect', onEvent)
      adapter?.on?.('accountsChanged', onEvent)
    } catch (_e) {
      void _e
    }

    pollTimer = setInterval(onEvent, 150)
    deadlineTimer = setTimeout(() => finish(''), timeoutMs)
  })
}

/** 建立 MetaMask TRON session（必须 popup 授权，不能仅从 session 读地址） */
async function ensureMetaMaskTronSession(adapter) {
  await adapter._checkWalletPromise

  if (adapter.readyState !== 'Found') {
    const err = new Error('NO_METAMASK_TRON')
    err.code = 'NO_METAMASK_TRON'
    throw err
  }

  if (isAdapterSessionReady(adapter)) {
    return String(adapter.address).trim()
  }

  if (adapter.connected && !isAdapterSessionReady(adapter)) {
    try {
      await adapter.disconnect()
    } catch (_e) {
      void _e
    }
    await sleepMs(200)
  }

  if (adapter.connecting) {
    const deadline = Date.now() + 20000
    while (adapter.connecting && Date.now() < deadline) {
      await sleepMs(120)
    }
    const addr = await waitForAdapterSessionReady(adapter, 5000)
    if (addr) return addr
  }

  try {
    await adapter.connect()
  } catch (e) {
    if (isWalletUserCancel(e)) throw e
    try {
      await adapter.disconnect()
    } catch (_e2) {
      void _e2
    }
    await sleepMs(200)
    await adapter.connect()
  }

  let addr = await waitForAdapterSessionReady(adapter, 60000)
  if (addr) return addr

  await resetMetaMaskTronAdapter()
  const fresh = await getMetaMaskTronAdapter()
  await fresh._checkWalletPromise
  await fresh.connect()
  addr = await waitForAdapterSessionReady(fresh, 60000)
  return addr
}

export function wrapMetaMaskTronAdapter(adapter) {
  return {
    isMetaMaskTron: true,
    chainId: TRON_CHAIN_ID,
    get tronAddressBase58() {
      return resolveTronDisplayAddress(String(adapter?.address || ''))
    },
    _metaMaskTronAdapter: adapter,
    signMessage: async (message) => {
      await ensureMetaMaskTronSession(adapter)
      return adapter.signMessage(String(message ?? ''))
    },
    signTransaction: async (transaction) => {
      await ensureMetaMaskTronSession(adapter)
      return adapter.signTransaction(transaction)
    },
    disconnect: () => adapter.disconnect(),
    get accountHex() {
      return resolveMetaMaskTronAccountHex(adapter?.address)
    },
  }
}

async function doConnectMetaMaskTron() {
  const adapter = await getMetaMaskTronAdapter()
  const b58 = await ensureMetaMaskTronSession(adapter)
  if (!b58 || !isAdapterSessionReady(adapterSingleton)) {
    const err = new Error('NO_TRON_ACCOUNT')
    err.code = 'NO_TRON_ACCOUNT'
    throw err
  }

  const account = resolveMetaMaskTronAccountHex(b58)
  if (!account) {
    const err = new Error('NO_TRON_ACCOUNT')
    err.code = 'NO_TRON_ACCOUNT'
    throw err
  }

  return {
    account,
    chainId: TRON_CHAIN_ID,
    provider: wrapMetaMaskTronAdapter(adapterSingleton),
    sessionKey: 'metamask',
  }
}

/** 页面刷新后静默恢复：仅当 adapter 已完整连接，不弹 MetaMask */
export async function tryRestoreMetaMaskTron() {
  if (typeof window === 'undefined') return null
  try {
    const adapter = await getMetaMaskTronAdapter()
    await adapter._checkWalletPromise
    if (!isAdapterSessionReady(adapter)) return null
    const b58 = String(adapter.address).trim()
    const account = resolveMetaMaskTronAccountHex(b58)
    if (!account) return null
    return {
      account,
      chainId: TRON_CHAIN_ID,
      provider: wrapMetaMaskTronAdapter(adapter),
      sessionKey: 'metamask',
    }
  } catch (_e) {
    return null
  }
}

export async function isMetaMaskTronAvailable() {
  if (typeof window === 'undefined') return false
  const { pickInjectProviderByWallet } = await import('./inject.mjs')
  const mm = pickInjectProviderByWallet({ key: 'metamask', type: 'inject' })
  if (!mm) return false
  try {
    const adapter = await getMetaMaskTronAdapter()
    await adapter._checkWalletPromise
    return adapter.readyState === 'Found'
  } catch (_e) {
    return false
  }
}

export function isMetaMaskTronProvider(provider) {
  return Boolean(provider?.isMetaMaskTron && provider._metaMaskTronAdapter)
}

export async function connectMetaMaskTron() {
  if (connectInflight) return connectInflight
  const task = doConnectMetaMaskTron().finally(() => {
    if (connectInflight === task) connectInflight = null
  })
  connectInflight = task
  return task
}

export async function disconnectMetaMaskTron() {
  connectInflight = null
  await resetMetaMaskTronAdapter()
}
